<script setup lang="ts">
import { storeToRefs } from "pinia";
import DropZone from "~/components/Common/Upload/DropZone.vue";
import FilePreview from "~/components/Common/Upload/FilePreview.vue";
import LoadButton from "~/components/Common/Buttons/LoadButton.vue";
import type { AttendeeInterface } from "~/utils/interfaces/sessions-interfaces";
import { useUploadDocumentStore } from "~/store/upload-document/upload-document.store";
import type { IUploadFile } from "~/utils/interfaces/documents-interfaces";
import type { BasicAttendeeModel } from "@simbelapp/order-sdk";
import type { SessionFileTypeEnum } from "~/utils/enums/session-file-type.enums";

const emit = defineEmits(["hide", "fileUploaded", "resetToggleShowMore"]);

const props = defineProps({
  defaultAttendees: {
    type: Array as PropType<AttendeeInterface[]>,
    required: false,
    default: () => [],
  },
  attendees: {
    type: Array as PropType<AttendeeInterface[]>,
    required: false,
    default: () => [],
  },
  documentType: {
    type: String,
    required: true,
  },
  itemType: {
    type: String,
    required: true,
  },
  itemId: {
    type: String,
    required: true,
  },
  bookingDetails: {
    type: Boolean,
    required: false,
  },
  footerUpload: {
    type: Boolean,
    default: () => true,
    required: false,
  },
  documentTypes: {
    type: Array as PropType<SessionFileTypeEnum[]>,
    required: false,
    default: undefined,
  },
  sessionWide: {
    type: Boolean,
    default: false,
    required: false,
  },
});

const uploadDocumentStore = useUploadDocumentStore();
const { files } = storeToRefs(uploadDocumentStore);
const canUpload = ref<boolean>(true);
const isUploading = ref<boolean>(false);

onMounted(() => {
  uploadDocumentStore.$reset();
});

/**
 * Handle file addition via input or drag & drop
 */
function handleFilesInput(event: Event): void {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    const attendee = props.defaultAttendees?.find((attendee) => attendee.id === props.itemId) as
      | BasicAttendeeModel
      | undefined;
    uploadDocumentStore.addFiles(Array.from(target.files), props.itemId, props.itemType, props.documentType, attendee);
    target.value = "";
  }
}

/**
 * Update file validity status
 */
function updateCanUpload(isValid: boolean): void {
  canUpload.value = isValid;
}

/**
 * Upload files and close modal
 */
async function uploadAndHide(): Promise<void> {
  if (!canUpload.value || isUploading.value) return;

  isUploading.value = true;

  try {
    const itemType = props.bookingDetails ? "booking" : undefined;
    await uploadDocumentStore.uploadFiles(files.value as IUploadFile[], itemType);
    emit("hide");
    emit("fileUploaded");
    emit("resetToggleShowMore");
  } catch (error) {
    console.error("Erreur lors de l'upload des fichiers:", error);
  } finally {
    isUploading.value = false;
  }
}

/**
 * Remove a file from the list
 */
function removeFileFromList(uploadFile: IUploadFile): void {
  uploadDocumentStore.removeFile(uploadFile.file);
}

/**
 * Update the document type of a file
 */
function updateDocumentFileType(fileId: string, newType: string): void {
  uploadDocumentStore.updateFileDocumentType(fileId, newType);
}

/**
 * Update the visibility of a file
 */
function updateDocumentVisibility(fileId: string, adminOnly: boolean): void {
  uploadDocumentStore.updateFileVisibility(fileId, adminOnly);
}

/**
 * Update the item associated with a file
 */
function updateDocumentFileItem(fileId: string, selectedUser: BasicAttendeeModel, itemType: string): void {
  uploadDocumentStore.updateFileItem(fileId, selectedUser, itemType);
}
</script>

<template>
  <div class="w-full flex flex-col items-center justify-center">
    <div class="max-w-[1095px] w-full flex-col flex justify-center items-center">
      <div class="pb-[48px] flex flex-col justify-center items-center w-[325px]">
        <DropZone v-slot="{ dropZoneActive }" class="drop_area" @files-dropped="handleFilesInput">
          <div class="w-full h-full">
            <label for="file-input">
              <img src="/icons/generic/upload.svg" class="mb-[20px]" :style="{ height: '64px', width: '64px' }" />
              <span v-if="dropZoneActive">
                <span>Déposez ici </span>
                <span class="text_underlined">pour l'ajouter</span>
              </span>
              <span v-else>
                <span>Glissez-déposez ici les documents à ajouter ou </span>
                <span class="text_underlined">Sélectionnez parmi vos fichiers</span>
              </span>
              <input id="file-input" type="file" multiple @change="handleFilesInput" />
            </label>
          </div>
        </DropZone>
        <span class="upload_info pt-[8px]">Formats de fichiers PDF, JPG, PNG - jusqu'à 10 Mo.</span>
      </div>

      <div
        v-show="files.length"
        class="w-full preview_zone"
        :class="{ 'max-h-56 h-full mb-8': !footerUpload, 'max-w-[640px]': sessionWide }"
      >
        <FilePreview
          :files="files as IUploadFile[]"
          :default-attendees="defaultAttendees"
          :attendees="attendees"
          :booking-details="bookingDetails"
          :document-types="documentTypes"
          :session-wide="sessionWide"
          :can-upload="canUpload"
          @update-document-file-type="updateDocumentFileType"
          @update-document-file-item="updateDocumentFileItem"
          @update-document-visibility="updateDocumentVisibility"
          @update-can-upload="updateCanUpload"
          @remove="removeFileFromList"
        />
      </div>

      <div v-if="footerUpload" class="w-full flex justify-center pt-[72px]">
        <LoadButton
          v-model="isUploading"
          text-confirm="Terminer"
          class="button__default w-[150px] mt-[16px]"
          :disabled="!canUpload || isUploading"
          :force-waiting="true"
          @click="uploadAndHide"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "/assets/scss/texts.scss";

.drop_area {
  @apply flex flex-col items-center;
  width: 100%;
  transition: 0.2s ease;
  border-style: dashed;
  border-width: 2px;
  border-radius: 16px;

  &[data-active="true"] {
    background: var(--grey-light);
  }

  &:hover {
    background: var(--grey-light);
  }
}

label {
  @extend .body_regular;
  @apply flex flex-col items-center py-[28px] px-[16px];
  cursor: pointer;

  span {
    @apply text-center;
    color: var(--text-secondary-color);
  }

  input[type="file"]:not(:focus-visible) {
    opacity: 0;
    position: absolute;
    z-index: -1;
  }

  .text_underlined {
    text-decoration: var(--text-secondary-color) underline;
    text-decoration: underline;
    color: var(--primary-blue-color);
  }
}

.upload_info {
  @extend .body_regular;
  @apply mt-[4px];
  color: var(--text-tertiary-color);
}

.image_list {
  display: flex;
  list-style: none;
  flex-wrap: wrap;
  padding: 0;
}

::-webkit-scrollbar {
  width: 6px;
  height: 64px;
  opacity: 0.2;
}

/* background color of the scrollbar */
::-webkit-scrollbar-track {
  background-color: var(--white-main);
}

/* color of the scrollbar thumb */
::-webkit-scrollbar-thumb {
  background-color: var(--black-dark);
  opacity: 0.2;
  border-radius: 6px;
}
</style>
